/**
 * Content Hub React Query Hooks
 *
 * Provides React Query hooks for Content Hub data fetching and mutations.
 * Includes proper cache invalidation and optimistic updates.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query'
import { contentHubService } from '@/lib/api/services/contentHub'
import type { ODataQueryParams } from '@/lib/api/odata/query-builder'
import type {
  ContentFolder,
  ContentFolderDetail,
  ContentFolderTree,
  ContentFoldersODataResponse,
  CreateContentFolderRequest,
  UpdateContentFolderRequest,
  MoveFolderRequest,
  ContentItem,
  ContentItemDetail,
  ContentItemsODataResponse,
  ContentItemUsageStats,
  CreateContentItemRequest,
  UpdateContentItemRequest,
  MoveContentItemRequest,
  DuplicateContentItemRequest,
  ContentItemType,
  ContentTag,
  ContentSearchParams,
  ContentSearchResult,
} from '@/lib/api/types'

// ============================================
// Query Keys
// ============================================

export const contentHubKeys = {
  all: ['content-hub'] as const,
  // Folders
  folders: () => [...contentHubKeys.all, 'folders'] as const,
  rootFolders: () => [...contentHubKeys.folders(), 'root'] as const,
  foldersList: (params?: ODataQueryParams) => [...contentHubKeys.folders(), 'list', params] as const,
  folderTree: () => [...contentHubKeys.folders(), 'tree'] as const,
  folder: (id: string) => [...contentHubKeys.folders(), 'detail', id] as const,
  // Items
  items: () => [...contentHubKeys.all, 'items'] as const,
  itemsList: (params?: ODataQueryParams) => [...contentHubKeys.items(), 'list', params] as const,
  itemsByFolder: (folderId: string, params?: ODataQueryParams) =>
    [...contentHubKeys.items(), 'folder', folderId, params] as const,
  itemsByType: (type: ContentItemType, params?: ODataQueryParams) =>
    [...contentHubKeys.items(), 'type', type, params] as const,
  item: (id: string) => [...contentHubKeys.items(), 'detail', id] as const,
  itemUsage: (id: string) => [...contentHubKeys.items(), 'usage', id] as const,
  recentItems: (limit?: number) => [...contentHubKeys.items(), 'recent', limit] as const,
  popularItems: (limit?: number) => [...contentHubKeys.items(), 'popular', limit] as const,
  // Tags
  tags: () => [...contentHubKeys.all, 'tags'] as const,
  // Search
  search: (params: ContentSearchParams) => [...contentHubKeys.all, 'search', params] as const,
  quickSearch: (query: string, limit?: number) =>
    [...contentHubKeys.all, 'quick-search', query, limit] as const,
}

// ============================================
// Folder Query Hooks
// ============================================

/**
 * Hook to fetch root folders (folders without a parent)
 */
export function useContentRootFolders(
  options?: Omit<UseQueryOptions<ContentFolder[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: contentHubKeys.rootFolders(),
    queryFn: () => contentHubService.getRootFolders(),
    ...options,
  })
}

/**
 * Hook to fetch a single folder with children and breadcrumbs
 */
export function useContentFolder(
  id: string,
  options?: Omit<UseQueryOptions<ContentFolderDetail, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: contentHubKeys.folder(id),
    queryFn: () => contentHubService.getFolder(id),
    enabled: !!id,
    ...options,
  })
}

/**
 * Hook to fetch the full folder tree (hierarchical structure)
 */
export function useContentFolderTree(
  options?: Omit<UseQueryOptions<ContentFolderTree[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: contentHubKeys.folderTree(),
    queryFn: () => contentHubService.getFolderTree(),
    ...options,
  })
}

/**
 * Hook to fetch folders with OData query support
 */
export function useContentFolders(
  params?: ODataQueryParams,
  options?: Omit<UseQueryOptions<ContentFoldersODataResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: contentHubKeys.foldersList(params),
    queryFn: () => contentHubService.getFolders(params),
    ...options,
  })
}

// ============================================
// Content Item Query Hooks
// ============================================

/**
 * Hook to fetch content items with OData query support
 */
export function useContentItems(
  params?: ODataQueryParams,
  options?: Omit<UseQueryOptions<ContentItemsODataResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: contentHubKeys.itemsList(params),
    queryFn: () => contentHubService.getItems(params),
    ...options,
  })
}

/**
 * Hook to fetch content items in a specific folder
 */
export function useContentItemsByFolder(
  folderId: string,
  params?: ODataQueryParams,
  options?: Omit<UseQueryOptions<ContentItemsODataResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: contentHubKeys.itemsByFolder(folderId, params),
    queryFn: () => contentHubService.getItemsByFolder(folderId, params),
    enabled: !!folderId,
    ...options,
  })
}

/**
 * Hook to fetch content items by type
 */
export function useContentItemsByType(
  type: ContentItemType,
  params?: ODataQueryParams,
  options?: Omit<UseQueryOptions<ContentItemsODataResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: contentHubKeys.itemsByType(type, params),
    queryFn: () => contentHubService.getItemsByType(type, params),
    enabled: !!type,
    ...options,
  })
}

/**
 * Hook to fetch a single content item with full details
 */
export function useContentItem(
  id: string,
  options?: Omit<UseQueryOptions<ContentItemDetail, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: contentHubKeys.item(id),
    queryFn: () => contentHubService.getItem(id),
    enabled: !!id,
    ...options,
  })
}

/**
 * Hook to fetch usage statistics for a content item
 */
export function useContentItemUsage(
  id: string,
  options?: Omit<UseQueryOptions<ContentItemUsageStats, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: contentHubKeys.itemUsage(id),
    queryFn: () => contentHubService.getItemUsage(id),
    enabled: !!id,
    ...options,
  })
}

/**
 * Hook to fetch recent content items
 */
export function useRecentContentItems(
  limit: number = 10,
  options?: Omit<UseQueryOptions<ContentItem[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: contentHubKeys.recentItems(limit),
    queryFn: () => contentHubService.getRecentItems(limit),
    ...options,
  })
}

/**
 * Hook to fetch popular content items
 */
export function usePopularContentItems(
  limit: number = 10,
  options?: Omit<UseQueryOptions<ContentItem[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: contentHubKeys.popularItems(limit),
    queryFn: () => contentHubService.getPopularItems(limit),
    ...options,
  })
}

// ============================================
// Tags Query Hook
// ============================================

/**
 * Hook to fetch all tags used in the Content Hub
 */
export function useContentTags(
  options?: Omit<UseQueryOptions<ContentTag[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: contentHubKeys.tags(),
    queryFn: () => contentHubService.getTags(),
    ...options,
  })
}

// ============================================
// Search Hooks
// ============================================

/**
 * Hook to search content items with debounce support
 */
export function useContentSearch(
  params: ContentSearchParams,
  options?: Omit<UseQueryOptions<ContentSearchResult, Error>, 'queryKey' | 'queryFn'> & {
    debounceMs?: number
  }
) {
  const { debounceMs = 300, ...queryOptions } = options ?? {}
  const [debouncedParams, setDebouncedParams] = useState(params)

  // Debounce the search parameters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedParams(params)
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [params, debounceMs])

  return useQuery({
    queryKey: contentHubKeys.search(debouncedParams),
    queryFn: () => contentHubService.search(debouncedParams),
    enabled: !!(debouncedParams.query || debouncedParams.types?.length || debouncedParams.tags?.length),
    ...queryOptions,
  })
}

/**
 * Hook for quick search (simple text search with debounce)
 */
export function useContentQuickSearch(
  query: string,
  limit: number = 20,
  options?: Omit<UseQueryOptions<ContentItem[], Error>, 'queryKey' | 'queryFn'> & {
    debounceMs?: number
  }
) {
  const { debounceMs = 300, ...queryOptions } = options ?? {}
  const [debouncedQuery, setDebouncedQuery] = useState(query)

  // Debounce the query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [query, debounceMs])

  return useQuery({
    queryKey: contentHubKeys.quickSearch(debouncedQuery, limit),
    queryFn: () => contentHubService.quickSearch(debouncedQuery, limit),
    enabled: debouncedQuery.length >= 2, // Only search if query is at least 2 characters
    ...queryOptions,
  })
}

// ============================================
// Folder Mutation Hooks
// ============================================

/**
 * Hook to create a new folder
 */
export function useCreateFolder(
  options?: Omit<UseMutationOptions<ContentFolder, Error, CreateContentFolderRequest, unknown>, 'mutationFn'>
) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: (data: CreateContentFolderRequest) => contentHubService.createFolder(data),
    onSuccess: (newFolder, variables, context) => {
      // Invalidate all folder queries including tree
      queryClient.invalidateQueries({ queryKey: contentHubKeys.folders() })
      queryClient.invalidateQueries({ queryKey: contentHubKeys.folderTree() })
      queryClient.invalidateQueries({ queryKey: contentHubKeys.rootFolders() })

      // If the folder has a parent, invalidate that parent's detail query
      if (newFolder.parentId) {
        queryClient.invalidateQueries({
          queryKey: contentHubKeys.folder(newFolder.parentId),
        })
      }

      // Call consumer's onSuccess if provided
      ;(options?.onSuccess as ((data: ContentFolder, variables: CreateContentFolderRequest, context: unknown) => void) | undefined)?.(newFolder, variables, context)
    },
  })
}

/**
 * Hook to update an existing folder
 */
type UpdateFolderVars = { id: string; data: UpdateContentFolderRequest }
export function useUpdateFolder(
  options?: Omit<UseMutationOptions<ContentFolder, Error, UpdateFolderVars, unknown>, 'mutationFn'>
) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: ({ id, data }: UpdateFolderVars) => contentHubService.updateFolder(id, data),
    onSuccess: (updatedFolder, variables, context) => {
      // Update the folder in cache
      queryClient.setQueryData(contentHubKeys.folder(variables.id), (old: ContentFolderDetail | undefined) => {
        if (!old) return old
        return { ...old, ...updatedFolder }
      })

      // Invalidate all folder queries including tree
      queryClient.invalidateQueries({ queryKey: contentHubKeys.folders() })
      queryClient.invalidateQueries({ queryKey: contentHubKeys.folderTree() })
      queryClient.invalidateQueries({ queryKey: contentHubKeys.rootFolders() })

      // Call consumer's onSuccess if provided
      ;(options?.onSuccess as ((data: ContentFolder, variables: UpdateFolderVars, context: unknown) => void) | undefined)?.(updatedFolder, variables, context)
    },
  })
}

/**
 * Hook to delete a folder
 */
export function useDeleteFolder(options?: Omit<UseMutationOptions<void, Error, string, unknown>, 'mutationFn'>) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: (id: string) => contentHubService.deleteFolder(id),
    onSuccess: (data, id, context) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: contentHubKeys.folder(id) })

      // Invalidate all folder queries including tree
      queryClient.invalidateQueries({ queryKey: contentHubKeys.folders() })
      queryClient.invalidateQueries({ queryKey: contentHubKeys.folderTree() })
      queryClient.invalidateQueries({ queryKey: contentHubKeys.rootFolders() })

      // Call consumer's onSuccess if provided
      ;(options?.onSuccess as ((data: void, variables: string, context: unknown) => void) | undefined)?.(data, id, context)
    },
  })
}

/**
 * Hook to move a folder to a new parent
 */
type MoveFolderVars = { id: string; data: MoveFolderRequest }
export function useMoveFolder(
  options?: Omit<UseMutationOptions<ContentFolder, Error, MoveFolderVars, unknown>, 'mutationFn'>
) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: ({ id, data }: MoveFolderVars) => contentHubService.moveFolder(id, data),
    onSuccess: (data, variables, context) => {
      // Invalidate all folder queries since the tree structure changed
      queryClient.invalidateQueries({ queryKey: contentHubKeys.folders() })
      queryClient.invalidateQueries({ queryKey: contentHubKeys.folderTree() })
      queryClient.invalidateQueries({ queryKey: contentHubKeys.rootFolders() })

      // Call consumer's onSuccess if provided
      ;(options?.onSuccess as ((data: ContentFolder, variables: MoveFolderVars, context: unknown) => void) | undefined)?.(data, variables, context)
    },
  })
}

// ============================================
// Content Item Mutation Hooks
// ============================================

/**
 * Hook to create a new content item
 */
export function useCreateContentItem(
  options?: UseMutationOptions<ContentItem, Error, CreateContentItemRequest>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateContentItemRequest) => contentHubService.createItem(data),
    onSuccess: (newItem) => {
      // Invalidate item list queries
      queryClient.invalidateQueries({ queryKey: contentHubKeys.items() })

      // Invalidate tags in case new tags were added
      queryClient.invalidateQueries({ queryKey: contentHubKeys.tags() })

      // If the item is in a folder, invalidate that folder's items
      if (newItem.folderId) {
        queryClient.invalidateQueries({
          queryKey: contentHubKeys.itemsByFolder(newItem.folderId),
        })
        queryClient.invalidateQueries({
          queryKey: contentHubKeys.folder(newItem.folderId),
        })
      }
    },
    ...options,
  })
}

/**
 * Hook to update an existing content item
 */
export function useUpdateContentItem(
  options?: UseMutationOptions<
    ContentItem,
    Error,
    { id: string; data: UpdateContentItemRequest }
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }) => contentHubService.updateItem(id, data),
    onSuccess: (updatedItem, variables) => {
      // Update the item in cache
      queryClient.setQueryData(contentHubKeys.item(variables.id), (old: ContentItemDetail | undefined) => {
        if (!old) return old
        return { ...old, ...updatedItem }
      })

      // Invalidate item list queries
      queryClient.invalidateQueries({ queryKey: contentHubKeys.items() })

      // Invalidate tags in case tags were changed
      queryClient.invalidateQueries({ queryKey: contentHubKeys.tags() })

      // Invalidate search results
      queryClient.invalidateQueries({
        queryKey: [...contentHubKeys.all, 'search'],
      })
    },
    ...options,
  })
}

/**
 * Hook to delete a content item
 */
export function useDeleteContentItem(options?: UseMutationOptions<void, Error, string>) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => contentHubService.deleteItem(id),
    onSuccess: (_data, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: contentHubKeys.item(id) })

      // Invalidate item list queries
      queryClient.invalidateQueries({ queryKey: contentHubKeys.items() })

      // Invalidate folder queries (for item counts)
      queryClient.invalidateQueries({ queryKey: contentHubKeys.folders() })

      // Invalidate tags
      queryClient.invalidateQueries({ queryKey: contentHubKeys.tags() })
    },
    ...options,
  })
}

/**
 * Hook to move a content item to a new folder
 */
export function useMoveContentItem(
  options?: UseMutationOptions<
    ContentItem,
    Error,
    { id: string; data: MoveContentItemRequest }
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }) => contentHubService.moveItem(id, data),
    onSuccess: (movedItem, variables) => {
      // Update the item in cache
      queryClient.setQueryData(contentHubKeys.item(variables.id), (old: ContentItemDetail | undefined) => {
        if (!old) return old
        return { ...old, ...movedItem }
      })

      // Invalidate item list queries
      queryClient.invalidateQueries({ queryKey: contentHubKeys.items() })

      // Invalidate folder queries (for item counts)
      queryClient.invalidateQueries({ queryKey: contentHubKeys.folders() })
    },
    ...options,
  })
}

/**
 * Hook to duplicate a content item
 */
export function useDuplicateContentItem(
  options?: UseMutationOptions<
    ContentItem,
    Error,
    { id: string; data: DuplicateContentItemRequest }
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }) => contentHubService.duplicateItem(id, data),
    onSuccess: (duplicatedItem) => {
      // Invalidate item list queries
      queryClient.invalidateQueries({ queryKey: contentHubKeys.items() })

      // If the duplicated item is in a folder, invalidate that folder
      if (duplicatedItem.folderId) {
        queryClient.invalidateQueries({
          queryKey: contentHubKeys.itemsByFolder(duplicatedItem.folderId),
        })
        queryClient.invalidateQueries({
          queryKey: contentHubKeys.folder(duplicatedItem.folderId),
        })
      }
    },
    ...options,
  })
}

// ============================================
// Tag Mutation Hooks
// ============================================

/**
 * Hook to add a tag to a content item
 */
export function useAddTag(
  options?: UseMutationOptions<ContentItem, Error, { itemId: string; tag: string }>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ itemId, tag }) => contentHubService.addTag(itemId, tag),
    onSuccess: (updatedItem, variables) => {
      // Update the item in cache
      queryClient.setQueryData(contentHubKeys.item(variables.itemId), (old: ContentItemDetail | undefined) => {
        if (!old) return old
        return { ...old, ...updatedItem }
      })

      // Invalidate tags query
      queryClient.invalidateQueries({ queryKey: contentHubKeys.tags() })

      // Invalidate item list queries
      queryClient.invalidateQueries({ queryKey: contentHubKeys.items() })
    },
    ...options,
  })
}

/**
 * Hook to remove a tag from a content item
 */
export function useRemoveTag(
  options?: UseMutationOptions<ContentItem, Error, { itemId: string; tag: string }>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ itemId, tag }) => contentHubService.removeTag(itemId, tag),
    onSuccess: (updatedItem, variables) => {
      // Update the item in cache
      queryClient.setQueryData(contentHubKeys.item(variables.itemId), (old: ContentItemDetail | undefined) => {
        if (!old) return old
        return { ...old, ...updatedItem }
      })

      // Invalidate tags query
      queryClient.invalidateQueries({ queryKey: contentHubKeys.tags() })

      // Invalidate item list queries
      queryClient.invalidateQueries({ queryKey: contentHubKeys.items() })
    },
    ...options,
  })
}

/**
 * Hook to add multiple tags to a content item
 */
export function useAddTags(
  options?: UseMutationOptions<ContentItem, Error, { itemId: string; tags: string[] }>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ itemId, tags }) => contentHubService.addTags(itemId, { tags }),
    onSuccess: (updatedItem, variables) => {
      // Update the item in cache
      queryClient.setQueryData(contentHubKeys.item(variables.itemId), (old: ContentItemDetail | undefined) => {
        if (!old) return old
        return { ...old, ...updatedItem }
      })

      // Invalidate tags query
      queryClient.invalidateQueries({ queryKey: contentHubKeys.tags() })

      // Invalidate item list queries
      queryClient.invalidateQueries({ queryKey: contentHubKeys.items() })
    },
    ...options,
  })
}

// ============================================
// Utility Hooks
// ============================================

/**
 * Hook to prefetch folder data (useful for hover states)
 */
export function usePrefetchFolder() {
  const queryClient = useQueryClient()

  return useCallback(
    (id: string) => {
      queryClient.prefetchQuery({
        queryKey: contentHubKeys.folder(id),
        queryFn: () => contentHubService.getFolder(id),
        staleTime: 1000 * 60 * 5, // 5 minutes
      })
    },
    [queryClient]
  )
}

/**
 * Hook to prefetch content item data (useful for hover states)
 */
export function usePrefetchContentItem() {
  const queryClient = useQueryClient()

  return useCallback(
    (id: string) => {
      queryClient.prefetchQuery({
        queryKey: contentHubKeys.item(id),
        queryFn: () => contentHubService.getItem(id),
        staleTime: 1000 * 60 * 5, // 5 minutes
      })
    },
    [queryClient]
  )
}

/**
 * Hook to invalidate all content hub cache
 */
export function useInvalidateContentHub() {
  const queryClient = useQueryClient()

  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: contentHubKeys.all })
  }, [queryClient])
}

/**
 * Hook to get the query client for advanced cache manipulation
 */
export function useContentHubQueryClient() {
  return useQueryClient()
}
